import axios from "axios";
import {applyLotProductSnapshot, applyLotCardSnapshot} from '../utils/lot-product-snapshot';
import {getPublicProductIdentifier, getPublicProductRouteIdentifier} from '../utils/public-product-identity';
export {getPublicProductIdentifier, getPublicProductRouteIdentifier};

const publicCatalogueRequests = new Map();
const authoritativeSalesEventFields = [
    'lotId', 'sellingMethod', 'lotStatus', 'status', 'endDate', 'effectiveEndDate',
    'scheduledEndDate', 'highestBid', 'totalBids', 'reserveMet'
];

function isAuctionSalesEvent(salesEvent) {
    return salesEvent?.sellingMethod?.toLowerCase() === 'auction';
}

function hasMeaningfulPublicProductValue(value) {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (typeof value === 'string') {
        return value.trim().length > 0;
    }

    return value !== null && value !== undefined;
}

function preferMeaningfulRefreshedValue(refreshedValue, currentValue) {
    return hasMeaningfulPublicProductValue(refreshedValue)
        ? refreshedValue
        : currentValue;
}

function getCardDetailFieldIds(cardDetails) {
    if (!Array.isArray(cardDetails)) {
        return [];
    }

    return cardDetails
        .map((detail) => detail?.field)
        .filter((field) => typeof field === 'string' && field.trim().length > 0);
}

function hasUserDefinedFieldsForCardDetails(userDefinedFieldsets, cardDetails) {
    const fieldIds = getCardDetailFieldIds(cardDetails);

    if (fieldIds.length === 0) {
        return hasMeaningfulPublicProductValue(userDefinedFieldsets);
    }

    if (!Array.isArray(userDefinedFieldsets) || userDefinedFieldsets.length === 0) {
        return false;
    }

    return fieldIds.every((fieldId) => userDefinedFieldsets.some((field) => field?.id === fieldId));
}

export function sanitizePublicSalesEvent(salesEvent) {
    if (!salesEvent) {
        return salesEvent;
    }

    const sanitizedSalesEvent = {
        ...salesEvent
    };

    delete sanitizedSalesEvent.reserve;
    delete sanitizedSalesEvent.reservePrice;
    delete sanitizedSalesEvent.reserveRevision;
    delete sanitizedSalesEvent.reserveHasBeenMet;
    delete sanitizedSalesEvent.reserveEditingPolicy;
    delete sanitizedSalesEvent.canEditReserve;
    delete sanitizedSalesEvent.reserveEditingRule;
    delete sanitizedSalesEvent.reserveEditingStage;
    delete sanitizedSalesEvent.winnerId;
    delete sanitizedSalesEvent.winnerCustomerId;
    delete sanitizedSalesEvent.winner;
    delete sanitizedSalesEvent.authoritativeCheckpoint;

    if (isAuctionSalesEvent(sanitizedSalesEvent)) {
        return sanitizedSalesEvent;
    }

    delete sanitizedSalesEvent.highestBid;
    delete sanitizedSalesEvent.totalBids;
    delete sanitizedSalesEvent.reserveMet;

    return sanitizedSalesEvent;
}

export function sanitizePublicProduct(product) {
    if (!product) {
        return product;
    }

    return {
        ...product,
        salesEvent: sanitizePublicSalesEvent(product.salesEvent)
    };
}

export function isPublicSalesEventMessageForCurrentLot(currentSalesEvent, message) {
    const currentLotId = currentSalesEvent?.lotId;
    const messageLotId = message?.lotId;

    return currentLotId === null
        || currentLotId === undefined
        || messageLotId === null
        || messageLotId === undefined
        || String(currentLotId) === String(messageLotId);
}

function normalisePublicStateVersion(value) {
    if (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0) {
        return String(value);
    }
    if (typeof value !== 'string' || !/^\d+$/.test(value.trim())) return null;
    return value.trim().replace(/^0+(?=\d)/, '');
}

export function comparePublicStateVersions(left, right) {
    const normalisedLeft = normalisePublicStateVersion(left);
    const normalisedRight = normalisePublicStateVersion(right);
    if (normalisedLeft === null || normalisedRight === null) return null;
    if (normalisedLeft.length !== normalisedRight.length) {
        return normalisedLeft.length > normalisedRight.length ? 1 : -1;
    }
    if (normalisedLeft === normalisedRight) return 0;
    return normalisedLeft > normalisedRight ? 1 : -1;
}

export function isPublicSalesEventStateCurrent(currentSalesEvent, incomingState) {
    const currentVersion = normalisePublicStateVersion(currentSalesEvent?.publicStateVersion);
    const incomingVersion = normalisePublicStateVersion(incomingState?.publicStateVersion);
    if (currentVersion === null) return true;
    if (incomingVersion === null) return false;
    const versionOrder = comparePublicStateVersions(incomingVersion, currentVersion);
    if (versionOrder !== 0) return versionOrder > 0;

    // A server checkpoint rereads committed state after the write. It may repair
    // a same-revision stale render, but it must never replace a different lot.
    if (incomingState?.authoritativeCheckpoint === true) {
        return isPublicSalesEventMessageForCurrentLot(currentSalesEvent, incomingState);
    }

    // Countdown seconds can legitimately change without a new database revision.
    // Bid, lot and close facts cannot: accepting two different values for the
    // same revision would let a delayed response roll the visible state back.
    return authoritativeSalesEventFields.every((field) => {
        const currentValue = currentSalesEvent?.[field];
        const incomingValue = incomingState?.[field];
        if (currentValue === null || currentValue === undefined
            || incomingValue === null || incomingValue === undefined) return true;
        if (field === 'lotId') return String(currentValue) === String(incomingValue);
        if (field === 'endDate' || field === 'effectiveEndDate' || field === 'scheduledEndDate') {
            const currentTime = new Date(currentValue).getTime();
            const incomingTime = new Date(incomingValue).getTime();
            return Number.isFinite(currentTime) && Number.isFinite(incomingTime)
                ? currentTime === incomingTime
                : currentValue === incomingValue;
        }
        return currentValue === incomingValue;
    });
}

export function mergePublicProductProps(currentProduct, incomingProduct) {
    if (!currentProduct || !incomingProduct) return sanitizePublicProduct(incomingProduct ?? currentProduct);

    const currentId = getPublicProductIdentifier(currentProduct);
    const incomingId = getPublicProductIdentifier(incomingProduct);
    if (!currentId || !incomingId || currentId !== incomingId) {
        return sanitizePublicProduct(incomingProduct);
    }

    const currentLotId = currentProduct.salesEvent?.lotId;
    const incomingLotId = incomingProduct.salesEvent?.lotId;
    if (currentLotId !== null && currentLotId !== undefined
        && incomingLotId !== null && incomingLotId !== undefined
        && String(currentLotId) !== String(incomingLotId)) {
        return sanitizePublicProduct(incomingProduct);
    }

    const preserveCurrentState = !!currentProduct.salesEvent
        && (!incomingProduct.salesEvent
            || !isPublicSalesEventStateCurrent(currentProduct.salesEvent, incomingProduct.salesEvent));
    const incomingHasViewerBid = Object.prototype.hasOwnProperty.call(incomingProduct, 'viewerBid');
    const currentHasViewerBid = Object.prototype.hasOwnProperty.call(currentProduct, 'viewerBid');
    const mergedProduct = {
        ...currentProduct,
        ...incomingProduct,
        salesEvent: preserveCurrentState ? currentProduct.salesEvent : incomingProduct.salesEvent
    };
    if (preserveCurrentState && !currentHasViewerBid) {
        delete mergedProduct.viewerBid;
    } else if (currentHasViewerBid && (preserveCurrentState || !incomingHasViewerBid)) {
        mergedProduct.viewerBid = currentProduct.viewerBid;
    }
    return sanitizePublicProduct(mergedProduct);
}

export function appendCatalogueNumberQuery(path, catalogueNumber) {
    if (!catalogueNumber) return path;
    const separator = String(path).includes('?') ? '&' : '?';
    return `${path}${separator}catalogueNumber=${encodeURIComponent(catalogueNumber)}`;
}

function secondsUntil(value, now) {
    const targetTime = value ? new Date(value).getTime() : NaN;
    if (!Number.isFinite(targetTime)) return 0;
    return Math.max(0, Math.ceil((targetTime - now) / 1000));
}

function normalisePublicSalesEventTiming(salesEvent, now = Date.now()) {
    if (!salesEvent) return salesEvent;

    const startTime = salesEvent.startDate ? new Date(salesEvent.startDate).getTime() : NaN;
    const endTime = salesEvent.endDate ? new Date(salesEvent.endDate).getTime() : NaN;

    return {
        ...salesEvent,
        ...(Number.isFinite(startTime) ? {secondsToStart: secondsUntil(salesEvent.startDate, now)} : {}),
        ...(Number.isFinite(endTime) ? {secondsRemaining: secondsUntil(salesEvent.endDate, now)} : {})
    };
}

export function getPublicCatalogueLotSummary(
    catalogue,
    {lotId = null, productIdentifier = null, now = Date.now()} = {}
) {
    const lots = Array.isArray(catalogue?.lots) ? catalogue.lots : [];
    const lot = lots.find((candidate) => lotId !== null
        && lotId !== undefined
        && String(candidate?.id) === String(lotId))
        ?? lots.find((candidate) => productIdentifier
            && String(candidate?.product?.id) === String(productIdentifier));

    if (!lot) return null;

    const endDate = lot.effectiveEndDate ?? lot.scheduledEndDate ?? catalogue?.endDate ?? null;

    return {
        lotId: lot.id,
        publicStateVersion: lot.publicStateVersion,
        cardSnapshot: lot.cardSnapshot,
        sellingMethod: lot.sellingMethod,
        lotStatus: lot.status,
        endDate,
        secondsRemaining: secondsUntil(endDate, now),
        secondsToStart: secondsUntil(catalogue?.startDate, now),
        highestBid: lot.highestBid,
        totalBids: lot.totalBids,
        reserveMet: lot.reserveMet
    };
}

export function fetchPublicCatalogueLotSummary({catalogueNumber, lotId, productIdentifier}) {
    if (!catalogueNumber) return Promise.resolve(null);

    const cacheKey = String(catalogueNumber);
    const cachedRequest = publicCatalogueRequests.get(cacheKey);
    let catalogueRequest;

    if (cachedRequest) {
        catalogueRequest = cachedRequest;
    } else {
        catalogueRequest = axios.get(
            `/api/v2/salesevents/catalogues/${encodeURIComponent(catalogueNumber)}`
        ).then(({data}) => data);
        publicCatalogueRequests.set(cacheKey, catalogueRequest);
        const clearCompletedRequest = () => {
            if (publicCatalogueRequests.get(cacheKey) === catalogueRequest) {
                publicCatalogueRequests.delete(cacheKey);
            }
        };
        catalogueRequest.then(clearCompletedRequest, clearCompletedRequest);
    }

    return catalogueRequest.then((catalogue) => getPublicCatalogueLotSummary(catalogue, {
        lotId,
        productIdentifier
    }));
}

export function mergePublicSalesEventSummary(existingSalesEvent, summary) {
    if (!summary) {
        return sanitizePublicSalesEvent(existingSalesEvent);
    }
    if (!isPublicSalesEventMessageForCurrentLot(existingSalesEvent, summary)) {
        return sanitizePublicSalesEvent(existingSalesEvent);
    }
    if (!isPublicSalesEventStateCurrent(existingSalesEvent, summary)) {
        return sanitizePublicSalesEvent(existingSalesEvent);
    }

    const hasHighestBid = Object.prototype.hasOwnProperty.call(summary, 'highestBid');
    const hasEndDate = Object.prototype.hasOwnProperty.call(summary, 'endDate');

    return sanitizePublicSalesEvent({
        ...existingSalesEvent,
        lotId: summary.lotId ?? existingSalesEvent?.lotId,
        publicStateVersion: summary.publicStateVersion ?? existingSalesEvent?.publicStateVersion,
        sellingMethod: summary.sellingMethod ?? existingSalesEvent?.sellingMethod,
        lotStatus: summary.lotStatus ?? existingSalesEvent?.lotStatus,
        endDate: hasEndDate ? summary.endDate : existingSalesEvent?.endDate,
        secondsRemaining: summary.secondsRemaining ?? existingSalesEvent?.secondsRemaining,
        secondsToStart: summary.secondsToStart ?? existingSalesEvent?.secondsToStart,
        highestBid: hasHighestBid ? summary.highestBid : existingSalesEvent?.highestBid,
        totalBids: summary.totalBids ?? existingSalesEvent?.totalBids,
        reserveMet: summary.reserveMet ?? existingSalesEvent?.reserveMet
    });
}

export function shouldRefreshPublicBidHistoryForSummary(currentSalesEvent, summary) {
    if (!summary || !isPublicSalesEventMessageForCurrentLot(currentSalesEvent, summary)) {
        return false;
    }
    if (!isPublicSalesEventStateCurrent(currentSalesEvent, summary)) return false;

    const hasTotalBids = Object.prototype.hasOwnProperty.call(summary, 'totalBids');
    const hasHighestBid = Object.prototype.hasOwnProperty.call(summary, 'highestBid');

    return (hasTotalBids && summary.totalBids !== currentSalesEvent?.totalBids)
        || (hasHighestBid && summary.highestBid !== currentSalesEvent?.highestBid);
}

export function shouldRefreshPublicBidHistoryForInvalidation(currentSalesEvent, message) {
    return message?.reason === 'bid_placed'
        && isPublicSalesEventMessageForCurrentLot(currentSalesEvent, message)
        && isPublicSalesEventStateCurrent(currentSalesEvent, message);
}

export function mergeRefreshedPublicProduct(currentProduct, refreshedProduct, latestSummary) {
    const refreshedHasViewerBid = Object.prototype.hasOwnProperty.call(refreshedProduct ?? {}, 'viewerBid');
    const currentHasViewerBid = Object.prototype.hasOwnProperty.call(currentProduct ?? {}, 'viewerBid');
    const publicProductRouteIdentifier = getPublicProductRouteIdentifier(
        refreshedProduct,
        getPublicProductRouteIdentifier(currentProduct)
    );
    const productTemplate = typeof refreshedProduct?.productTemplate === 'object' && refreshedProduct?.productTemplate !== null
        ? refreshedProduct.productTemplate
        : currentProduct?.productTemplate;
    const refreshedSalesEventIsCurrent = isPublicSalesEventStateCurrent(
        currentProduct?.salesEvent,
        refreshedProduct?.salesEvent
    );
    const hasRefreshedSalesEventLinks = !!refreshedProduct?._links?.salesEvent && !!refreshedProduct?._links?.salesEventLot;
    const lockedCatalogueLotId = currentProduct?.catalogueContext?.lotId;
    const refreshedMatchesCatalogueLot = !lockedCatalogueLotId
        || String(refreshedProduct?.salesEvent?.lotId) === String(lockedCatalogueLotId);
    const preserveCatalogueSalesEvent = !!lockedCatalogueLotId && !refreshedMatchesCatalogueLot;
    const preserveNewerCurrentSalesEvent = !!currentProduct?.salesEvent && !refreshedSalesEventIsCurrent;
    const hasSalesEvent = preserveCatalogueSalesEvent || preserveNewerCurrentSalesEvent
        ? !!currentProduct?.salesEvent
        : hasRefreshedSalesEventLinks;
    const refreshedSalesEvent = refreshedProduct?.salesEvent
        ? {
            ...refreshedProduct.salesEvent,
            scheduledEndDate: refreshedProduct.salesEvent.scheduledEndDate
                ?? currentProduct?.salesEvent?.scheduledEndDate
        }
        : refreshedProduct?.salesEvent;
    const baseSalesEvent = hasSalesEvent
        ? (preserveCatalogueSalesEvent
            ? currentProduct.salesEvent
            : (preserveNewerCurrentSalesEvent
                ? currentProduct.salesEvent
                : (refreshedSalesEvent ?? currentProduct?.salesEvent ?? null)))
        : null;
    const summaryMatchesCurrentLot = !latestSummary?.lotId
        || !baseSalesEvent?.lotId
        || latestSummary.lotId === baseSalesEvent.lotId;
    const compatibleSummary = summaryMatchesCurrentLot ? latestSummary : null;
    const cardDetails = preferMeaningfulRefreshedValue(
        refreshedProduct?.cardDetails,
        currentProduct?.cardDetails
    );
    const userDefinedFieldsets = hasUserDefinedFieldsForCardDetails(refreshedProduct?.userDefinedFieldsets, cardDetails)
        ? refreshedProduct.userDefinedFieldsets
        : currentProduct?.userDefinedFieldsets;
    const mergedProduct = {
        ...currentProduct,
        ...refreshedProduct,
        id: publicProductRouteIdentifier,
        productTemplate,
        uuid: refreshedProduct?.uuid ?? currentProduct?.uuid,
        productUuid: refreshedProduct?.productUuid ?? currentProduct?.productUuid,
        cardDetails,
        userDefinedFieldsets,
        ...(refreshedHasViewerBid
            ? {viewerBid: refreshedProduct.viewerBid}
            : currentHasViewerBid
                ? {viewerBid: currentProduct.viewerBid}
                : {}),
        salesEvent: hasSalesEvent
            ? normalisePublicSalesEventTiming(
                mergePublicSalesEventSummary(baseSalesEvent, compatibleSummary) ?? baseSalesEvent
            )
            : null
    };

    getCardDetailFieldIds(cardDetails).forEach((fieldId) => {
        if (
            !hasMeaningfulPublicProductValue(refreshedProduct?.[fieldId])
            && hasMeaningfulPublicProductValue(currentProduct?.[fieldId])
        ) {
            mergedProduct[fieldId] = currentProduct[fieldId];
        }
    });

    return applyLotCardSnapshot(applyLotProductSnapshot(sanitizePublicProduct(mergedProduct)),
        compatibleSummary?.cardSnapshot ?? mergedProduct.lotCardSnapshot);
}
